import { createReducer, on } from '@ngrx/store';
import moment from 'moment';

import { REPORT_ACTIONS } from '@containers/reports/report/ag-grid-reports/ag-grid-base/models/constants';
import { IReport, IReportState } from '@shared/types/report';

import {
    add_dashboard_dates,
    add_dashboard_dayparts,
    add_dashboard_destination,
    add_dashboard_header,
    add_dashboard_hours,
    add_report,
    add_report_failure,
    add_report_success,
    add_report_to_history,
    clear_selected_row_essential_data,
    get_reports,
    get_reports_failure,
    get_reports_success,
    get_stats_success,
    make_default,
    make_default_failure,
    make_default_success,
    remove_report,
    remove_reports_from_history,
    reset_default,
    reset_default_failure,
    reset_default_success,
    reset_report_etl_statistics,
    select_report,
    select_report_failure,
    select_report_success,
    set_dashboard_last_run_filter,
    set_report_etl_statistics_failure,
    set_report_etl_statistics_success,
    set_report_filters,
    set_report_filters_failure,
    set_report_filters_success,
    set_report_url,
    set_report_url_failure,
    set_report_url_success,
    set_selected_row_essential_data,
} from '../actions/reports.action';

const initialState: IReportState = {
    reports: [],
    reportsHistory: [
        {
            title: 'REPORTING',
            route: '/',
            id: null,
            parentId: null,
            reportLink: false,
        },
    ],
    selectedRowEssentialData: null,
    reportFilters: [],
    reportUrl: '',
    submittedTime: '',
    enLocaleSubmittedTime: '',
    selectedReport: null,
    reportAction: {
        onprogress: false,
        type: '',
    },
    speedOfService: {
        speedOfServiceData: [],
    },
    salesAnalysis: {
        salesAnalysisData: [],
    },
    dashboard: {
        stats: [],
        dates: [],
        dayparts: [],
        destinations: [],
        hours: [],
        header: [],
        show_history_data: true,
        execute_automatically: null,
        title: {
            original: 'Dashboard',
            i18nTitle: null,
        },
        hasActiveLaborMatrix: false,
    },
    salesAndLabor: {
        hours: [],
        header: [],
    },
    etlStatistic: {
        jobStatus: '',
        jobStarted: '',
        jobFinished: '',
        jobDuration: '',
        needShow: false,
    },
    dashboardLastRunFilter: {
        date: {},
        sites: [],
    },
};

const _reportsReducer = createReducer(
    initialState,

    on(get_reports, (state): IReportState => state),
    on(get_reports_success, (state, { reports }): IReportState => ({ ...state, reports })),
    on(get_reports_failure, (state): IReportState => ({ ...state, reports: [] })),

    on(
        get_stats_success,
        (state, { stats, title, execute_automatically, show_history_data, hasActiveLaborMatrix }): IReportState => {
            const sortedStats = [...stats].sort((a, b) => a.index - b.index);
            return {
                ...state,
                dashboard: {
                    ...state.dashboard,
                    title,
                    stats: sortedStats,
                    execute_automatically,
                    show_history_data,
                    hasActiveLaborMatrix,
                },
            };
        },
    ),

    on(add_dashboard_dates, (state, { dates }): IReportState => {
        return Object.assign({}, state, {
            dashboard: {
                ...state.dashboard,
                dates,
            },
        });
    }),
    on(add_dashboard_dayparts, (state, { dayparts }): IReportState => {
        return Object.assign({}, state, {
            dashboard: {
                ...state.dashboard,
                dayparts,
            },
        });
    }),
    on(add_dashboard_destination, (state, { destination }): IReportState => {
        return Object.assign({}, state, {
            dashboard: {
                ...state.dashboard,
                destination,
            },
        });
    }),

    on(add_dashboard_hours, (state, { hours }): IReportState => {
        return Object.assign({}, state, {
            dashboard: {
                ...state.dashboard,
                hours,
            },
        });
    }),

    on(add_dashboard_header, (state, { header }): IReportState => {
        return Object.assign({}, state, {
            dashboard: {
                ...state.dashboard,
                header,
            },
        });
    }),

    on(set_dashboard_last_run_filter, (state, { date, sites }): IReportState => {
        return {
            ...state,
            dashboardLastRunFilter: {
                date,
                sites,
            },
        };
    }),

    on(
        select_report,
        (state): IReportState => ({
            ...state,
            reportAction: {
                ...state.reportAction,
                type: REPORT_ACTIONS.SELECT_REPORT,
                onprogress: true,
            },
        }),
    ),

    on(
        select_report_failure,
        (state): IReportState => ({
            ...state,
            selectedReport: null,
            reportFilters: [],
            reportAction: {
                ...state.reportAction,
                onprogress: false,
            },
        }),
    ),
    on(select_report_success, (state, { report, filters }): IReportState => {
        const id = (report as IReport)._id || (report as IReport).id;
        const index = state.reportsHistory.findIndex((history) => history.id === id);
        const reportHistory = add_to_history(state, index, report);

        if (filters) {
            return {
                ...state,
                selectedReport: report,
                reportFilters: filters,
                reportsHistory: reportHistory,
                reportAction: {
                    ...state.reportAction,
                    onprogress: false,
                },
            };
        }

        return {
            ...state,
            selectedReport: report,
            reportsHistory: reportHistory,
            reportAction: {
                ...state.reportAction,
                onprogress: false,
            },
        };
    }),
    on(
        remove_report,
        (state): IReportState => ({
            ...state,
            selectedReport: null,
            reportFilters: [],
            reportUrl: '',
            reportAction: {
                ...state.reportAction,
                type: REPORT_ACTIONS.REMOVE_REPORT,
            },
        }),
    ),

    on(set_report_filters, (state): IReportState => state),
    on(set_report_filters_failure, (state): IReportState => ({ ...state, reportFilters: [] })),
    on(set_report_filters_success, (state, { reportFilters }): IReportState => ({ ...state, reportFilters })),

    on(set_report_url, (state): IReportState => state),
    on(set_report_url_failure, (state): IReportState => ({ ...state, reportUrl: '' })),
    on(set_report_url_success, (state, { reportUrl }): IReportState => {
        const submittedMoment = moment();
        const submitTime = submittedMoment.format('MM/DD/YYYY HH:mm:ss');

        return { ...state, reportUrl, submittedTime: submitTime };
    }),

    on(
        set_report_etl_statistics_failure,
        (state): IReportState => ({
            ...state,
            etlStatistic: {
                jobStatus: '',
                jobStarted: '',
                jobFinished: '',
                jobDuration: '',
                needShow: false,
            },
        }),
    ),

    on(
        set_report_etl_statistics_success,
        (state, { jobStatus, jobStarted, jobFinished, jobDuration }): IReportState => ({
            ...state,
            etlStatistic: { jobStatus, jobStarted, jobFinished, jobDuration, needShow: true },
        }),
    ),
    on(
        reset_report_etl_statistics,
        (state): IReportState => ({
            ...state,
            etlStatistic: { jobStatus: '', jobStarted: '', jobFinished: '', jobDuration: '', needShow: false },
        }),
    ),

    on(add_report, (state): IReportState => state),
    on(add_report_failure, (state): IReportState => state),
    on(add_report_success, (state): IReportState => state),

    on(add_report_to_history, (state, { reportOptions, filters }): IReportState => {
        const id = reportOptions._id || reportOptions.id;
        const index = state.reportsHistory.findIndex((report) => report.id === id);
        const reportsHistory = add_to_history(state, index, reportOptions, filters);
        return { ...state, reportsHistory: reportsHistory };
    }),
    on(remove_reports_from_history, (state, { index }): IReportState => {
        state.reportsHistory.length = index + 1;
        return state;
    }),

    on(set_selected_row_essential_data, (state, { reportExecutionData, reportId }): IReportState => {
        const index = state.reportsHistory.findIndex((report) => report.id === reportId);
        if (index !== -1) {
            state.reportsHistory[index].reportExecutionData = reportExecutionData;
        }
        return {
            ...state,
            selectedRowEssentialData: reportExecutionData,
        };
    }),

    on(clear_selected_row_essential_data, (state): IReportState => {
        return {
            ...state,
            selectedRowEssentialData: null,
        };
    }),

    on(
        make_default,
        (state): IReportState => ({
            ...state,
            reportAction: {
                ...state.reportAction,
                onprogress: true,
                type: REPORT_ACTIONS.MAKE_DEFAULT,
            },
        }),
    ),

    on(make_default_success, (state, { report, gridOptions }): IReportState => {
        const reports = state.reports.map((r) => (r._id === report._id ? { ...r, report_config: { gridOptions } } : r));
        return {
            ...state,
            reportAction: {
                ...state.reportAction,
                onprogress: false,
            },
            selectedReport: {
                ...state.selectedReport,
                report_config: { gridOptions },
            },
            reports,
        };
    }),

    on(
        make_default_failure,
        (state): IReportState => ({
            ...state,
            reportAction: {
                ...state.reportAction,
                onprogress: false,
            },
        }),
    ),

    on(
        reset_default,
        (state): IReportState => ({
            ...state,
            reportAction: {
                ...state.reportAction,
                onprogress: true,
                type: REPORT_ACTIONS.RESET_DEFAULT,
            },
        }),
    ),

    on(reset_default_success, (state, { report }): IReportState => {
        const reports = state.reports.map((r) => (r._id === report._id ? { ...r, report_config: {} } : r));
        return {
            ...state,
            reportAction: {
                ...state.reportAction,
                onprogress: false,
            },
            selectedReport: {
                ...state.selectedReport,
                report_config: {},
            },
            reports,
        };
    }),

    on(
        reset_default_failure,
        (state): IReportState => ({
            ...state,
            reportAction: {
                ...state.reportAction,
                onprogress: false,
            },
        }),
    ),
);

const add_to_history = (state, index, reportOptions, filters?) => {
    let reportsHistory = [...state.reportsHistory];
    if (index === -1) {
        const id = reportOptions._id || reportOptions.id;
        reportsHistory = [
            ...reportsHistory,
            {
                title: reportOptions.custom_report_title || reportOptions.name,
                isDetailView: reportOptions.is_detail_view,
                route: '/custom-reports/report/' + id,
                reportLink: true,
                id,
                parentId: state.reportsHistory[state.reportsHistory.length - 1]?.id,
                additional_path_parameters: reportOptions.additional_path_parameters,
                filters: filters,
            },
        ];
    } else {
        reportsHistory.splice(index + 1);
        reportsHistory[index] = {
            ...reportsHistory[index],
            filters: filters || reportsHistory[index].filters,
        };
    }
    return reportsHistory;
};

export function reportsReducer(state, action) {
    return _reportsReducer(state, action);
}
